// src/components/CaseStudyDetail.tsx
import React from "react";
import DataOps from "../assets/DataOps.jpg";
import DataOpsCompanies from "../assets/DataOpsCompanies.png";
import MLOps from "../assets/MLOps.jpg";
import MLOpsCompanies from "../assets/MLopsCompanies.png";
import FinanceTransformation from "../assets/FinanceTransformaion.jpg";
import FinanceTransformationCompanies from "../assets/FInanceTrasformationCompanies.png";
import redbell from "../assets/Redbell.svg";
import shieldTick from "../assets/ShieldTick.svg";
import flatGradiant from "../assets/Flat_Gradient.svg";
import Navbar from "./navbar";
import Footer from "./Footer";
import { ArrowLeft } from "lucide-react"; 
import { useNavigate, useParams } from "react-router-dom";

interface ListItemProps {
  text: string;
  variant?: "issue" | "approach" | "outcome";
}

const icons = {
  issue: redbell,
  approach: shieldTick,
  outcome: flatGradiant,
};

const ListItem: React.FC<ListItemProps> = ({ text, variant = "issue" }) => {
  return (
    <div className="flex items-center gap-3 w-full">
      <img
        src={icons[variant]}
        alt={variant}
        className="w-8 h-8 md:w-10 md:h-10 object-contain flex-shrink-0"
      />
      <p className="flex-1 text-gray-700 text-base md:text-xl font-normal font-['Nunito Sans'] leading-relaxed md:leading-loose tracking-tight">
        {text}
      </p>
    </div>
  );
};

const TechStack: React.FC<{ logo: string }> = ({ logo }) => (
  <div className="w-full flex flex-col gap-4 md:gap-6">
    <div className="px-3 py-2 md:px-4 md:py-2 bg-slate-200 flex justify-start items-center">
      <div className="self-stretch justify-start text-gray-700 text-lg md:text-2xl font-semibold font-['Nunito Sans'] leading-relaxed md:leading-loose tracking-tight">
        TECH STACK
      </div>
    </div>
    <div className="flex justify-center">
      <div className="w-full max-w-[536px] h-auto md:h-[132px] bg-white rounded-md flex items-center justify-center p-2 md:p-0">
        <img src={logo} alt="tech logo" className="w-full max-w-[500px] h-auto md:h-[120px] object-contain" />
      </div>
    </div>
  </div>
);

interface CaseStudyDetailProps {
  id: string;
  title: string;
  description: string;
  problems: string[];
  approaches: string[];
  outcomes: string[];
  logos: string;
  image: string;
}

const CaseStudyDetail: React.FC<CaseStudyDetailProps> = ({
  title,
  description,
  problems,
  approaches,
  outcomes,
  logos,
  image,
}) => {
  return (
    <div className="p-4 sm:p-6 md:p-8 lg:p-10 bg-slate-50 rounded-xl md:rounded-2xl lg:rounded-3xl flex flex-col gap-6 md:gap-8 lg:gap-12">
      {/* Title + Problems */}
      <div className="flex flex-col lg:flex-row gap-6 md:gap-8 lg:gap-12">
        <div className="flex-1 flex flex-col gap-4 md:gap-6">
          <h2 className="text-blue-900 text-xl sm:text-2xl md:text-3xl font-semibold font-['IBM Plex Sans Condensed'] leading-7 md:leading-9 lg:leading-10 tracking-tight">
            {title}
          </h2>
          {description && (
            <p className="text-zinc-700 text-base md:text-xl font-medium font-['Nunito Sans'] leading-relaxed md:leading-loose tracking-tight">
              {description}
            </p>
          )}

          <div className="flex flex-col gap-2 md:gap-3">
            {problems.map((issue, idx) => (
              <ListItem key={idx} text={issue} variant="issue" />
            ))}
          </div>
        </div>

        {/* Image */}
        <img
          className="flex-1 w-full lg:w-auto h-auto max-h-[300px] sm:max-h-[350px] md:max-h-[400px] lg:h-[420px] lg:max-w-[536px] rounded-lg md:rounded-xl object-cover"
          src={image}
          alt="Case study"
        />
      </div>

      {/* Approach + Outcomes + Tech stack */}
      <div className="flex flex-col lg:flex-row gap-6 md:gap-8 lg:gap-12">
        {/* Approach */}
        <div className="flex-1 p-4 md:p-6 bg-zinc-100 rounded-xl md:rounded-2xl flex flex-col gap-4 md:gap-6">
          <h3 className="text-zinc-700 text-xl md:text-2xl font-semibold font-['Nunito Sans']">
            Approach
          </h3>
          <div className="flex flex-col gap-2 md:gap-3">
            {approaches.map((approach, idx) => (
              <ListItem key={idx} text={approach} variant="approach" />
            ))}
          </div>
        </div>

        {/* Outcomes + Tech stack */}
        <div className="flex flex-col gap-6 md:gap-8 w-full lg:w-auto">
          <div className="w-full lg:w-[536px] p-4 md:p-6 bg-sky-100 rounded-xl md:rounded-2xl flex flex-col gap-4 md:gap-6">
            <h3 className="text-zinc-700 text-xl md:text-2xl font-semibold font-['Nunito Sans']">
              Outcomes
            </h3>
            <div className="flex flex-col gap-2 md:gap-3">
              {outcomes.map((outcome, idx) => (
                <ListItem key={idx} text={outcome} variant="outcome" />
              ))}
            </div>
          </div>

          <TechStack logo={logos} />
        </div>
      </div>
    </div>
  );
};

// Wrapper for multiple case studies
const CaseStudiesdetails: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const caseStudies: CaseStudyDetailProps[] = [
    {
      id: "finance-transformation",
      title: "Finance Transformation for a Pharma Leader",
      description: "Helping Pharma giant in achieving day 1 book closure & hassle free reporting which was lagging due to:",
      problems: [
        "Multiple copies of data",
        "Unreliable Data Quality",
        "Multiple manual touch points",
        "No Master Data Management",
        "High dependency on IT for reporting",
      ],
      approaches: [
        "Conducted workshops to identify paint point",
        "Adopted methodology of AUTOMATE OR ELEMINATE for manual touch points",
        "Design Medix based MDM system for master data management",
        "Redesign Ingestion pipelines to be more robust and self-recoverable",
        "Designed and implemented Data Quality checks and Data Health Dashboard to proactively monitor and maintain Data Quality",
        "Developed Power BI based semantic model for enabling Self-Serve capability in organization",
      ],
      outcomes: [
        "90% manual intervention eliminated & touchless pipeline target achieved",
        "Data Quality Health improvement",
        "Proper Governance model for maintain Finance Data Lake",
        "TAT for report/insights delivery improved by 50%",
        "Day 1 financial book closure achieved",
      ],
      logos: FinanceTransformationCompanies,
      image: FinanceTransformation,
    },
    {
      id: "dataops",
      title: "DataOps & Managed Services Framework",
      description: "",
      problems: ["Build a process based, scalable Managed Services setup which will not only maintain the existing system but also provide enhancement support"],
      approaches: [
        "Setup a Global Data Ops team with a mix of technical expertise and support levels(L1,L2,L3)",
        "Automated all monitoring and incident generation on ServiceNow",
        "Created a clear workflow of Incident and Enhancement lifecycle",
        "Implemented proactive monitoring, Data Quality checks",
        "Automated deployment pipelines using DevOps"
      ],
      outcomes: ["Automated deployment pipelines using DevOps", "Improved SLA compliance","Robust governance & visibility","Increasing in platform usage"],
      logos: MLOpsCompanies,
      image: DataOps,
    },
    {
      id: "mlops",
      title: "MLOps-Enabled Databricks Lakehouse Design",
      description: "",
      problems: ["Australia based financial services provider wanted to modernize their data stack and migrate all the workloads and reporting from Onprem SQL Server to Azure Data Lake"],
      approaches: ["Designed modern data architecture based on Azure recommended tech stack",
        "Leveraged Terraform to automate Infra provisioning",
        "Design and developed Delta lake powered Lakehouse using ADF and Databricks",
        "Created CI/CD pipelines for automated deployments",
        "Created frameworks for loading data in Medallion architecture",
        "Enabled MLOps on the platform for hassle-free Data science project delivery",
      ],
      outcomes: ["10+ Usecases onboarded on the new Azure based data platform in 12 months", "40% faster pipeline runtimes",
        "Proper Governance model for maintain Finance Data Lake", "Reusable MLOps framework delivered"
      ],
      logos: DataOpsCompanies,
      image: MLOps,
    },
  ];
  
  if (id) {
    const caseStudy = caseStudies.find(cs => cs.id === id);
    if (!caseStudy) return <div>Case study not found</div>;
    
    return (
      <div className="flex flex-col gap-8 md:gap-12 lg:gap-16 px-4 sm:px-6 md:px-12 lg:px-24 py-8 md:py-16 lg:py-28">
        <CaseStudyDetail {...caseStudy} />
      </div>
    );
  }

  return (
    <>
      <Navbar/> 
      <button
        onClick={() => navigate("/")}
        className="flex items-center mt-6 md:mt-8 lg:mt-10 mb-4 mx-4 sm:mx-6 md:mx-12 lg:mx-24 gap-2 text-blue-900 hover:text-blue-700 font-semibold text-lg md:text-xl lg:text-2xl"
      >
        <ArrowLeft className="w-6 h-5 md:w-8 md:h-6 lg:w-10 lg:h-7" /> Back
      </button>   
      <div className="flex flex-col gap-8 md:gap-12 lg:gap-16 px-4 sm:px-6 md:px-12 lg:px-24 pt-4 pb-12 md:pb-16 lg:pb-20">
        {caseStudies.map((cs, idx) => (
          <CaseStudyDetail key={idx} {...cs} />
        ))}
      </div>
      <Footer/>
    </>  
  );
};

export default CaseStudiesdetails;